import { DEFAULT_CARD_THEME, type Decoration, type ResolvedCardTheme } from './cardTheme';

/**
 * AW6 — old2 テーマプリセット (Sakura / Forest / Pop / Minimal) の v2 移植。
 *
 * 旧 `old2/apps/admin/src/features/cardPrint/themes.ts` の 4 preset を ResolvedCardTheme 形式で
 * 同梱する。旧は装飾 SVG を `?raw` import していたが、v2 は build 依存を避けるため SVG 文字列を
 * 本ファイルにインライン (sanitizeSvg/svgToDataUrl/rasterizeDecoration が解釈する素の SVG)。
 *
 * M7 master とは別系統の「ワンクリック適用 preset」。preset 適用は印刷ジョブ単位の見た目変更で、
 * master 永続化したい場合は適用後に master role が「保存 (M7)」する (schema subset に丸められる)。
 *
 * 旧との差分:
 *   - `id` / `name` / `builtIn` は preset メタとして保持 (`CardPreset.id` / `.label`)
 *   - tintWith は old2 の `primary`/`secondary` をそのまま保持 (rasterizer が accent/secondary に解決)
 */

export interface CardPreset {
  id: string;
  label: string;
  theme: ResolvedCardTheme;
}

// --- 装飾 SVG (old2 assets/cardDecorations/* をインライン) ---

const SAKURA_LEFT_BAND = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 55" preserveAspectRatio="none"><defs><linearGradient id="sakuraBand" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="currentColor" stop-opacity="0.95"/><stop offset="1" stop-color="currentColor" stop-opacity="0.7"/></linearGradient></defs><rect x="0" y="0" width="5" height="55" fill="url(#sakuraBand)"/><circle cx="2.5" cy="6" r="0.7" fill="#ffffff" fill-opacity="0.9"/><circle cx="2.5" cy="20" r="0.5" fill="#ffffff" fill-opacity="0.8"/><circle cx="2.5" cy="34" r="0.6" fill="#ffffff" fill-opacity="0.85"/><circle cx="2.5" cy="48" r="0.4" fill="#ffffff" fill-opacity="0.75"/></svg>`;

const SAKURA_CORNER_TR = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 16" preserveAspectRatio="none"><ellipse cx="20" cy="2" rx="14" ry="6" fill="currentColor" fill-opacity="0.85"/><ellipse cx="26" cy="0" rx="6" ry="3" fill="currentColor" fill-opacity="0.5"/><circle cx="14" cy="6" r="1.6" fill="#ffffff" fill-opacity="0.9"/><circle cx="20" cy="9" r="1.2" fill="#ffffff" fill-opacity="0.7"/></svg>`;

const SAKURA_TITLE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><g transform="translate(8 8)"><g transform="rotate(0)"><path d="M0 -6 C 2 -3, 2 -2, 0 0 C -2 -2, -2 -3, 0 -6 Z" fill="currentColor"/></g><g transform="rotate(72)"><path d="M0 -6 C 2 -3, 2 -2, 0 0 C -2 -2, -2 -3, 0 -6 Z" fill="currentColor"/></g><g transform="rotate(144)"><path d="M0 -6 C 2 -3, 2 -2, 0 0 C -2 -2, -2 -3, 0 -6 Z" fill="currentColor"/></g><g transform="rotate(216)"><path d="M0 -6 C 2 -3, 2 -2, 0 0 C -2 -2, -2 -3, 0 -6 Z" fill="currentColor"/></g><g transform="rotate(288)"><path d="M0 -6 C 2 -3, 2 -2, 0 0 C -2 -2, -2 -3, 0 -6 Z" fill="currentColor"/></g><circle cx="0" cy="0" r="1.2" fill="#ffffff"/></g></svg>`;

const FOREST_HEADER = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 91 4" preserveAspectRatio="none"><rect x="0" y="0" width="91" height="2" fill="currentColor"/><rect x="0" y="2.6" width="91" height="0.4" fill="currentColor" fill-opacity="0.5"/></svg>`;

const FOREST_CORNER_BL = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><g fill="currentColor" fill-opacity="0.6"><path d="M2 22 C 2 12, 8 6, 18 4 C 14 10, 12 16, 8 22 Z"/><path d="M4 22 C 4 18, 6 14, 10 12 C 9 16, 7 19, 5 22 Z" fill-opacity="0.4"/></g><path d="M2 22 L 18 4" stroke="currentColor" stroke-width="0.4" stroke-opacity="0.5" fill="none"/></svg>`;

const POP_STRIPES = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 91 6" preserveAspectRatio="none"><defs><pattern id="popStripes" width="4" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(-12)"><rect x="0" y="0" width="2" height="6" fill="currentColor"/><rect x="2" y="0" width="2" height="6" fill="#1F1F1F"/></pattern></defs><rect x="0" y="0" width="91" height="6" fill="url(#popStripes)"/></svg>`;

const POP_SPEECH = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 14"><path d="M2 2 H 26 V 10 H 8 L 4 13 V 10 H 2 Z" fill="currentColor" stroke="#1F1F1F" stroke-width="0.6"/></svg>`;

const POP_BURST = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><g transform="translate(12 12)" fill="currentColor"><polygon points="0,-11 2,-3 11,0 2,3 0,11 -2,3 -11,0 -2,-3"/><circle cx="0" cy="0" r="3.5" fill="#1F1F1F"/><circle cx="0" cy="0" r="2" fill="#FFFDF5"/></g></svg>`;

const MINIMAL_LINE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 55" preserveAspectRatio="none"><rect x="0" y="0" width="1" height="55" fill="currentColor"/></svg>`;

const SAKURA_DECORATIONS: Decoration[] = [
  {
    slot: 'left-side-band',
    svgSource: SAKURA_LEFT_BAND,
    x_mm: 0,
    y_mm: 0,
    width_mm: 5,
    height_mm: 55,
    tintWith: 'primary',
  },
  {
    slot: 'corner-top-right',
    svgSource: SAKURA_CORNER_TR,
    x_mm: 65,
    y_mm: 0,
    width_mm: 26,
    height_mm: 8,
    tintWith: 'secondary',
    opacity: 0.85,
  },
  {
    slot: 'title-icon',
    svgSource: SAKURA_TITLE_ICON,
    x_mm: 45.8,
    y_mm: 0.6,
    width_mm: 3,
    height_mm: 3,
    tintWith: 'primary',
    opacity: 0.7,
  },
];

const FOREST_DECORATIONS: Decoration[] = [
  {
    slot: 'header-band',
    svgSource: FOREST_HEADER,
    x_mm: 0,
    y_mm: 0,
    width_mm: 91,
    height_mm: 4,
    tintWith: 'primary',
  },
  {
    slot: 'corner-bottom-left',
    svgSource: FOREST_CORNER_BL,
    x_mm: 45.5,
    y_mm: 43,
    width_mm: 12,
    height_mm: 12,
    tintWith: 'primary',
    opacity: 0.4,
  },
];

const POP_DECORATIONS: Decoration[] = [
  {
    slot: 'header-band',
    svgSource: POP_STRIPES,
    x_mm: 0,
    y_mm: 0,
    width_mm: 91,
    height_mm: 4,
    tintWith: 'primary',
  },
  {
    slot: 'corner-bottom-right',
    svgSource: POP_BURST,
    x_mm: 78,
    y_mm: 42,
    width_mm: 13,
    height_mm: 13,
    tintWith: 'secondary',
  },
  {
    slot: 'title-icon',
    svgSource: POP_SPEECH,
    x_mm: 45.7,
    y_mm: 0.5,
    width_mm: 6,
    height_mm: 3,
    tintWith: 'primary',
    opacity: 0.6,
  },
];

const MINIMAL_DECORATIONS: Decoration[] = [
  {
    slot: 'left-side-band',
    svgSource: MINIMAL_LINE,
    x_mm: 45.5,
    y_mm: 0,
    width_mm: 0.5,
    height_mm: 55,
    tintWith: 'primary',
  },
];

export const SAKURA_THEME: ResolvedCardTheme = {
  backgroundColor: '#FFF7F5',
  accent: '#FF7BA9',
  secondary: '#7BD3EA',
  textHeading: '#3A2438',
  textColor: '#5A4A55',
  fontFamily: 'zenkaku',
  logoUrl: '',
  categoryColors: { danger: '#E03E5C', resource: '#3CA9C9', notice: '#E8A23C' },
  border: { color: '#FFD1DC', width_mm: 0.3 },
  layout: {
    photoSide: 'left',
    photoWidth_mm: 40,
    textPadding_mm: 2,
    titleFontSize_pt: 11,
    bodyFontSize_pt: 8,
  },
  decorations: SAKURA_DECORATIONS,
};

export const FOREST_THEME: ResolvedCardTheme = {
  backgroundColor: '#F4F8F4',
  accent: '#2F6B4F',
  secondary: '#D9A441',
  textHeading: '#1A2E1A',
  textColor: '#3F4A3F',
  fontFamily: 'mincho',
  logoUrl: '',
  categoryColors: { danger: '#B5392E', resource: '#2F6B4F', notice: '#D9A441' },
  border: { color: '#C8DBC4', width_mm: 0.3 },
  layout: {
    photoSide: 'left',
    photoWidth_mm: 40,
    textPadding_mm: 2,
    titleFontSize_pt: 11,
    bodyFontSize_pt: 8,
  },
  decorations: FOREST_DECORATIONS,
};

export const POP_THEME: ResolvedCardTheme = {
  backgroundColor: '#FFFDF5',
  accent: '#FFD23F',
  secondary: '#3AAED8',
  textHeading: '#1F1F1F',
  textColor: '#1F1F1F',
  fontFamily: 'zenkaku',
  logoUrl: '',
  categoryColors: { danger: '#EE4266', resource: '#3AAED8', notice: '#FFD23F' },
  border: { color: '#1F1F1F', width_mm: 0.5 },
  layout: {
    photoSide: 'left',
    photoWidth_mm: 40,
    textPadding_mm: 2.5,
    titleFontSize_pt: 11,
    bodyFontSize_pt: 8,
  },
  decorations: POP_DECORATIONS,
};

export const MINIMAL_THEME: ResolvedCardTheme = {
  backgroundColor: '#FFFFFF',
  accent: '#0F766E',
  secondary: '#71717A',
  textHeading: '#18181B',
  textColor: '#3F3F46',
  fontFamily: 'zenkaku',
  logoUrl: '',
  categoryColors: { danger: '#DC2626', resource: '#2563EB', notice: '#F59E0B' },
  border: { color: '#E4E4E7', width_mm: 0.2 },
  layout: {
    photoSide: 'left',
    photoWidth_mm: 40,
    textPadding_mm: 2.5,
    titleFontSize_pt: 11,
    bodyFontSize_pt: 8,
  },
  decorations: MINIMAL_DECORATIONS,
};

/** 「既定」preset (DEFAULT_CARD_THEME のラベル付き) — リセット相当。 */
export const DEFAULT_PRESET_THEME: ResolvedCardTheme = DEFAULT_CARD_THEME;

export const CARD_PRESETS: CardPreset[] = [
  { id: 'default', label: '既定', theme: DEFAULT_PRESET_THEME },
  { id: 'sakura', label: 'Sakura', theme: SAKURA_THEME },
  { id: 'forest', label: 'Forest', theme: FOREST_THEME },
  { id: 'pop', label: 'Pop', theme: POP_THEME },
  { id: 'minimal', label: 'Minimal', theme: MINIMAL_THEME },
];

export function getPresetById(id: string): CardPreset | undefined {
  return CARD_PRESETS.find((p) => p.id === id);
}
